// Global, Promise-based delete confirmation.
//
// USAGE:
//   import { confirmDelete } from "./lib/confirmDelete";
//   if (await confirmDelete()) { /* really delete */ }
//
// The first call shows a centered "Are you sure?" modal with a
// "Don't ask again" checkbox. If the user opts out, every subsequent
// call resolves immediately to `true` without showing the dialog.
//
// The preference can be reset from Settings via
// `setSkipDeleteConfirm(false)` so users can re-enable the prompts.

const SKIP_KEY = "tiplingo_skip_delete_confirm_v1";

/** True when the user has opted out of "Are you sure?" prompts. */
export function shouldSkipDeleteConfirm() {
  try {
    return localStorage.getItem(SKIP_KEY) === "1";
  } catch (e) {
    console.error("confirmDelete: localStorage unavailable", e);
    return false;
  }
}

export function setSkipDeleteConfirm(value) {
  try {
    if (value) localStorage.setItem(SKIP_KEY, "1");
    else localStorage.removeItem(SKIP_KEY);
  } catch (e) {
    console.error("confirmDelete: localStorage unavailable", e);
  }
}

// --- internal pub/sub used by <DeleteConfirmDialog /> ---
const listeners = new Set();
let pending = null;

/** Subscribe to changes in the pending confirmation. */
export function subscribeConfirmDelete(callback) {
  listeners.add(callback);
  if (pending) callback(pending);
  return () => listeners.delete(callback);
}

/** Called by the modal when the user clicks Cancel or Delete. */
export function resolveConfirmDelete(value, dontAskAgain) {
  if (!pending) return;
  if (dontAskAgain && value) setSkipDeleteConfirm(true);
  const { resolve } = pending;
  pending = null;
  listeners.forEach((cb) => cb(null));
  resolve(value);
}

/**
 * Show the confirmation dialog and return a Promise<boolean>.
 * Resolves `true` when the user confirms, `false` when they cancel.
 * Bypasses the dialog (and resolves `true` instantly) when the user
 * previously chose "Don't ask again".
 */
export function confirmDelete(opts = {}) {
  if (shouldSkipDeleteConfirm()) return Promise.resolve(true);
  return new Promise((resolve) => {
    pending = { opts, resolve };
    listeners.forEach((cb) => cb(pending));
  });
}
